// storm-allow-pin-palette
// PIN-DATA: BG_FOR_COLOR map (lines below) is LOCKED per D-69 + alembic 0007.
// The `// storm-allow-pin-palette` directive above silences the chrome ESLint
// rule (storm/no-legacy-palette-in-chrome) for this file. Locked-grep gate
// honored via `LOCKED:OVERRIDE:` (start-of-line + colon-delimited; matches
// the gate's regex `^LOCKED:OVERRIDE:` at chrome-pin-locked-grep.sh:31) on
// the commit that introduced this comment (Phase 9 Wave 2 pre-swap).
/**
 * DimensionChip (UI-SPEC W8) — status chip showing per-dimension state.
 *
 * Color matches the sprite fill in the map (D-69). Lucide icons double the
 * color signal per NFR-A11Y-1.
 */
import { FormattedMessage } from "react-intl";
import { CheckCircle2, AlertTriangle, XCircle, Circle } from "lucide-react";
import type { Dimension } from "./pinSource";
import type { DimensionColor } from "../../api/assets";

interface DimensionChipProps {
  dimension: Dimension;
  color: DimensionColor;
}

const LABEL_ID: Record<Dimension, string> = {
  cleaning: "LABEL_DIMENSION_CLEANING",
  coating: "LABEL_DIMENSION_COATING",
  cover_level: "LABEL_DIMENSION_COVER_LEVEL",
  pipe_blockage: "LABEL_DIMENSION_PIPE_BLOCKAGE",
  structure: "LABEL_DIMENSION_STRUCTURE",
};

const BG_FOR_COLOR: Record<DimensionColor, string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-red-500",
  grey: "bg-gray-400",
};

const ICON_FOR_COLOR: Record<DimensionColor, typeof CheckCircle2> = {
  green: CheckCircle2,
  yellow: AlertTriangle,
  red: XCircle,
  grey: Circle,
};

export function DimensionChip({ dimension, color }: DimensionChipProps) {
  const Icon = ICON_FOR_COLOR[color];
  const bg = BG_FOR_COLOR[color];
  return (
    <span
      data-testid={`dim-chip-${dimension}-${color}`}
      className={`inline-flex items-center gap-1 ps-3 pe-3 h-7 rounded-full text-sm font-semibold text-white ${bg}`}
    >
      <Icon size={12} aria-hidden="true" />
      <FormattedMessage id={LABEL_ID[dimension]} />
    </span>
  );
}
